<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          We use your date of birthday to confirm identity and to ensure you are
          over age of 18.
        </p>
      </div>
    </help-drawer>
    <v-form name="DOB" class="form" data-test-id="dob-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.DOB.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="dob-header">
              <span class="headerlight">
                What's your <strong>date of birth?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your date of birth...</p>
        </div>
        
        <!-- Show data submitted message only when not syncing and form is hidden -->
        <data-submitted-message
          v-if="isDOBComplete && formHidden && !isSyncing"
          :submitted="isDOBComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col
              class="step-form-container d-flex justify-center flex-column"
            >
              <label for="dob" class="required" data-test-id="dob-label">Date of Birth</label>
              <v-text-field
                v-model="dob"
                :placeholder="INPUT_DATE_FMT"
                outlined
                @input="$v.dob.$touch()"
                @blur="standardizeDate()"
                :error-messages="dobErrors"
                :backgroundColor="inputBackgroundColor"
                dense
                inputmode="numeric"
                maxlength="8"
                data-test-id="dob-input"
              >
                <template v-slot:append>
                  <span class="cal-icon" data-test-id="calendar-icon">
                    <v-icon small color="#9b9b9b">fas fa-calendar-alt</v-icon>
                  </span>
                </template>
              </v-text-field>
              <!--<v-menu
                v-model="activatorMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dobString"
                    placeholder=""
                    readonly
                    outlined
                    @input="$v.dob.$touch()"
                    @blur="$v.dob.$touch()"
                    :error-messages="dobErrors"
                    :backgroundColor="inputBackgroundColor"
                    dense
                    v-on="on"
                    class="locked-date-input"
                  >
                    <template v-slot:append>
                      <span
                        class="cal-icon"
                        @click="activatorMenu = !activatorMenu"
                      >
                        <v-icon small color="#9b9b9b">fas fa-calendar-alt</v-icon>
                      </span>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="dob"
                  :max="maxDate"
                  @input="activatorMenu = false"
                  :color="primaryColor"
                ></v-date-picker>
              </v-menu>-->
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="dob-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import {
  MENUS,
  formatDate,
  ISO_DATE_SHORT,
  INPUT_DATE_FMT,
  validInputDate,
  US_DATE_SHORT,
} from '@/common/constants';
import { required } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'DateOfBirth',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    dob: null,
    activatorMenu: false,
    maxDate: formatDate(window.moment()),
    MENUS: MENUS,
  }),
  validations() {
    return {
      dob: {
        required,
        validInputDate,
      },
      validationGroup: ['dob'],
    };
  },
  computed: {
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.DOB.id);
      return page && page.status === 'IN_PROGRESS';
    },
    
    // Is date of birth already completed?
    isDOBComplete() {
      return this.isPageComplete(MENUS.CLIENT.DOB.id);
    },
    
    // Dynamic  errors */
    dobErrors() {
      let errors = '';

      if (!this.$v.dob.$dirty) return errors;
      if (!this.$v.dob.required) errors = 'Date of Birth is required';
      if (!this.$v.dob.validInputDate)
        errors = 'Date of Birth must be a valid date (MMDDYYYY)';

      return errors;
    },

    dobString() {
      /*return this.dob ? formatDate(this.dob, US_DATE_SHORT) : "";*/
      return this.dob
        ? window.moment(this.dob, US_DATE_SHORT, true).format(ISO_DATE_SHORT)
        : null;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },
  methods: {
    // Standardize a date from input format to US Readable
    standardizeDate() {
      this.$v.dob.$touch();
      if (validInputDate(this.dob)) {
        this.dob = this.dob
          ? window.moment(this.dob, INPUT_DATE_FMT, true).format(US_DATE_SHORT)
          : null;
      }
    },

    // Activate date picker menu
    activate() {
      this.activatorMenu = true;
    },

    /* Nav to Next Page in flow */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      // ISO Short date
      const data = {
        dob: this.dobString,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isDOBComplete) {
        this.formHidden = true;
      }
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    }
  },
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if question is complete
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isDOBComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.DOB.id);

    // Initialize form data for current interview
    this.formHidden = this.isDOBComplete;
    this.INPUT_DATE_FMT = INPUT_DATE_FMT;
  },
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
